import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  CartContactType,
  CartItemType,
  CartStateType,
  CartTotalsType,
  ProductType,
} from '@/types/types';

// Types

// Action types
type AddItemAction = {
  type: 'ADD_ITEM';
  payload: {
    product: ProductType;
    quantity?: number;
    lockQuantity?: boolean;
    hideLineItem?: boolean;
  };
};

type RemoveItemAction = {
  type: 'REMOVE_ITEM';
  payload: {
    itemId: string;
  };
};

type UpdateQuantityAction = {
  type: 'UPDATE_QUANTITY';
  payload: {
    itemId: string;
    quantity: number;
  };
};

type ClearDiscountAction = {
  type: 'CLEAR_DISCOUNT';
};

type SetDiscountAction = {
  type: 'SET_DISCOUNT';
  payload: string;
};

type ClearCartAction = {
  type: 'CLEAR_CART';
};

type SetCartAction = {
  type: 'SET_CART';
  payload: CartStateType;
};

type SetContactAction = {
  type: 'SET_CONTACT';
  payload: CartContactType;
};

type SetDeliveryAddressAction = {
  type: 'SET_DELIVERY_ADDRESS';
  payload: CartStateType['deliveryAddress'];
};

type SetBillingAddressAction = {
  type: 'SET_BILLING_ADDRESS';
  payload: CartStateType['billingAddress'];
};

type SetBillingSameAsDeliveryAction = {
  type: 'SET_BILLING_SAME_AS_DELIVERY';
  payload: boolean;
};

type SetSoleProprietorAction = {
  type: 'SET_SOLE_PROPRIETOR';
  payload: boolean;
};

type SetHasLocalChangesAction = {
  type: 'SET_HAS_LOCAL_CHANGES';
  payload: boolean;
};

export type CartAction =
  | AddItemAction
  | RemoveItemAction
  | UpdateQuantityAction
  | ClearDiscountAction
  | ClearCartAction
  | SetCartAction
  | SetContactAction
  | SetDeliveryAddressAction
  | SetBillingAddressAction
  | SetBillingSameAsDeliveryAction
  | SetSoleProprietorAction
  | SetHasLocalChangesAction
  | SetDiscountAction;

// Initial cart state
const DEFAULT_CART_STATE: CartStateType = {
  id: null,
  lineItems: [],
  totalPrice: 0,
  totalTax: 0,
  subtotalPrice: 0,
  deliveryPrice: 0,
  discountAmount: 0,
  discountCode: '',
  isSoleProprietor: true,
  billingIsSameAsDelivery: true,
  deliveryAddress: null,
  billingAddress: null,
};

// ===== CORE CART STATE =====

// UI state atoms
export const isCartSlideOutOpenAtom = atom<boolean>(false);

// The base cart state atom with local storage persistence
export const cartStateAtom = atomWithStorage<CartStateType>(
  'cart-state',
  DEFAULT_CART_STATE
);

// A store to manage products independently
export const productStoreAtom = atomWithStorage<Record<
  string,
  ProductType
> | null>('product-store', null);

// i want to attach the product store to the cart state
export const cartStateWithProductsAtom = atom<
  CartStateType & {
    lineItems: (CartItemType & {
      product: ProductType | undefined;
    })[];
    hasCardMachine?: boolean;
    hasLocalChanges?: boolean;
  }
>((get) => {
  const cart = get(cartStateAtom);
  const products = get(productStoreAtom);

  return {
    ...cart,
    lineItems: cart.lineItems.map((item) => ({
      ...item,
      product: products?.[item.id],
    })) as (CartItemType & { product: ProductType | undefined })[],
    hasCardMachine: cart.lineItems.some((item) => {
      const product = products?.[item.id];
      return product?.categorySlug === 'card-machines';
    }),
  };
});

// has localchanges
export const hasLocalChangesAtom = atom<boolean>(false);

// ===== CART OPERATIONS LOADERS =====

export const isRemovingOneItemAtom = atom<boolean | string | number>(false);
export const isAddingOneAtom = atom<boolean | string | number | string[]>(
  false
);
export const isRemovingLineItemAtom = atom<boolean | string | number>(false);

// ===== THIRD PARTY INTEGRATIONS =====

export const mixpanelUserIdentityAtom = atomWithStorage<string | null>(
  'mixpanelUserIdentity',
  null
);

export const cartIdWithStorageAtom = atomWithStorage<string | null>(
  'cart-id',
  null
);

export const billingSameAsDeliveryAtom = atom<string>('same');
export const isSoleProprietorAtom = atomWithStorage<boolean>(
  'isSoleProprietor',
  true
);

// ===== DERIVED ATOMS =====

// Cart items as a separate atom for components that only need the items
export const cartItemsAtom = atom<CartItemType[]>(
  (get) => get(cartStateWithProductsAtom).lineItems
);

// Total number of items in cart (for cart icon)
export const cartItemsQuantityAtom = atom((get) => {
  const items = get(cartItemsAtom);
  return items.reduce(
    (acc, item) =>
      !(item.lockQuantity && item.hideLineItem) ? acc + item.quantity : acc,
    0
  );
});

// Calculate cart totals for components that only need the totals
export const cartTotalsAtom = atom<CartTotalsType>((get) => {
  const cart = get(cartStateWithProductsAtom);
  return {
    subtotal: cart.subtotalPrice,
    tax: cart.totalTax,
    delivery: cart.deliveryPrice,
    discount: cart.discountAmount,
    total: cart.totalPrice,
  };
});

// Add a product to the product store
export const addProductAtom = atom(null, (get, set, product: ProductType) => {
  const currentProducts = get(productStoreAtom);
  const id = product.variantGid || product.productId;
  set(productStoreAtom, {
    ...currentProducts,
    [id as string]: product,
  });
});

// Remove a product from the product store
export const removeProductAtom = atom(null, (get, set, productId: string) => {
  const currentProducts = get(productStoreAtom);
  const { [productId]: _, ...remainingProducts } = currentProducts || {};
  set(productStoreAtom, remainingProducts);
});

// ===== CART OPERATIONS =====

// Cart operations atom - acts as the reducer
export const cartOperationsAtom = atom(null, (get, set, action: CartAction) => {
  const currentCart = get(cartStateAtom);
  let newCart: CartStateType;

  switch (action.type) {
    case 'SET_CART': {
      newCart = action.payload;
      break;
    }

    case 'ADD_ITEM': {
      const { product, quantity = 1, lockQuantity } = action.payload;

      // Use the prepared addProductAtom
      set(addProductAtom, product);

      const existingItemIndex = currentCart.lineItems.findIndex(
        (item) =>
          item.id === product.productId || item.id === product.variantGid
      );

      let lineItems: any;
      if (existingItemIndex >= 0) {
        // Update existing item
        lineItems = [...currentCart.lineItems];
        lineItems[existingItemIndex] = {
          ...lineItems[existingItemIndex],
          quantity: lineItems[existingItemIndex].quantity + quantity,
        };
      } else {
        const productId = product.variantGid || product.productId || ``;
        // Add new item
        lineItems = [
          ...currentCart.lineItems,
          { id: productId, quantity, lockQuantity },
        ];
      }

      //get all products from the product store
      const products = get(productStoreAtom);

      newCart = calculateCartTotals(
        {
          ...currentCart,
          lineItems,
          discountAmount: 0,
          hasLocalChanges: true,
          isSoleProprietor: true,
        },
        products || {}
      );

      break;
    }

    case 'REMOVE_ITEM': {
      const { itemId } = action.payload;

      // Use the prepared removeProductAtom
      set(removeProductAtom, itemId);

      const lineItems = currentCart.lineItems.filter(
        (item) => item.id !== itemId
      );

      const product = get(productStoreAtom);

      newCart = calculateCartTotals(
        {
          ...currentCart,
          lineItems,
          discountAmount: 0,
          hasLocalChanges: true,
        },
        product || {}
      );
      break;
    }

    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload;

      const lineItems = currentCart.lineItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      );
      const product = get(productStoreAtom);
      newCart = calculateCartTotals(
        {
          ...currentCart,
          lineItems,

          discountAmount: 0,
          hasLocalChanges: true,
        },
        product || {}
      );
      break;
    }

    // set discount code
    case 'SET_DISCOUNT': {
      newCart = {
        ...currentCart,
        discountCode: action.payload,
      };
      break;
    }
    case 'CLEAR_DISCOUNT': {
      const product = get(productStoreAtom);
      newCart = calculateCartTotals(
        {
          ...currentCart,
          discountCode: '',
          discountAmount: 0,
        },
        product || {}
      );
      break;
    }

    case 'SET_CONTACT': {
      newCart = {
        ...currentCart,
        contact: action.payload,
        hasLocalChanges: true,
      };
      break;
    }
    case 'CLEAR_CART': {
      newCart = DEFAULT_CART_STATE;
      break;
    }

    case 'SET_DELIVERY_ADDRESS': {
      const address = {
        ...(currentCart.deliveryAddress || {}),
        ...action.payload,
      };
      newCart = {
        ...currentCart,
        deliveryAddress: address,
        ...(currentCart.billingIsSameAsDelivery && {
          billingAddress: address,
        }),
        hasLocalChanges: true,
      };
      break;
    }
    //  billing same as delivery
    case 'SET_BILLING_SAME_AS_DELIVERY': {
      newCart = {
        ...currentCart,
        billingIsSameAsDelivery: action.payload,
        ...(action.payload && {
          billingAddress: currentCart.deliveryAddress,
        }),
        ...(!action.payload && {
          billingAddress: null,
        }),

        hasLocalChanges: true,
      };
      break;
    }

    case 'SET_BILLING_ADDRESS': {
      newCart = {
        ...currentCart,
        billingAddress: action.payload,

        hasLocalChanges: true,
      };
      break;
    }

    case 'SET_SOLE_PROPRIETOR': {
      newCart = {
        ...currentCart,
        isSoleProprietor: action.payload,
      };
      break;
    }
    case 'SET_HAS_LOCAL_CHANGES': {
      newCart = {
        ...currentCart,
        hasLocalChanges: action.payload,
      };
      break;
    }

    default: {
      // Type assertion using exhaustive check pattern
      // const _exhaustiveCheck: never = action;
      return;
    }
  }

  set(cartStateAtom, newCart);
});

// ===== HELPER FUNCTIONS =====

// Helper function to calculate cart totals
function calculateCartTotals(
  cart: CartStateType,
  products: {
    [key: string]: ProductType | undefined;
  }
): CartStateType {
  const TAX_RATE = 0.15; // 15% VAT

  // Step 1: Calculate VAT-inclusive subtotal
  const vatInclusiveSubtotal = cart.lineItems.reduce((sum, item) => {
    const product = products[item.id];
    const priceWithTax = product?.price || 0;
    return sum + priceWithTax * item.quantity;
  }, 0);

  // Step 2: Apply discount to VAT-inclusive subtotal
  const discountedTotal = Math.max(
    0,
    vatInclusiveSubtotal - cart.discountAmount
  );

  // Step 3: Extract VAT from the discounted total
  const totalTax = (discountedTotal * TAX_RATE) / (1 + TAX_RATE);
  const subtotalExclTax = discountedTotal - totalTax;

  // Step 4: Determine delivery fee
  const deliveryPrice = cart.lineItems.some((item) => {
    const product = products[item.id];
    return ['card-machines', 'point-of-sale'].includes(
      product?.categorySlug as string
    );
  })
    ? 0
    : cart.lineItems.reduce((max, item) => {
        const product = products[item.id];
        return Math.max(max, product?.shippingCost || 0);
      }, 0);

  // Step 5: Final total
  const totalPrice = discountedTotal + deliveryPrice;

  return {
    ...cart,
    subtotalPrice: subtotalExclTax,
    totalTax,
    deliveryPrice,
    totalPrice,
  };
}

// Compatibility with old cart atom
export const cartAtom = atom<CartStateType | null>((get) => get(cartStateAtom));
